import { useState } from "react";
import type { ClientPlatform, PlatformVersion, PlatformVersionRange, PlatformVersionRangeBound } from "../../types/experiment";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { X, Plus, ChevronDown, ChevronRight } from "lucide-react";

interface PlatformVersionEditorProps {
  value: ClientPlatform;
  onChange: (value: ClientPlatform) => void;
}

function parseVersion(str: string): { major: number; minor?: number; build?: string } | undefined {
  const trimmed = str.trim();
  if (!trimmed) return undefined;
  const parts = trimmed.split(".");
  const major = parseInt(parts[0]);
  if (isNaN(major)) return undefined;
  return {
    major,
    minor: parts[1] !== undefined ? parseInt(parts[1]) : undefined,
    build: parts[2] ?? undefined,
  };
}

function formatVersion(v?: { major: number; minor?: number; build?: string }): string {
  if (!v) return "";
  if (v.minor !== undefined) {
    return v.build !== undefined ? `${v.major}.${v.minor}.${v.build}` : `${v.major}.${v.minor}`;
  }
  return `${v.major}`;
}

function PlatformVersionRangeBoundInput({
  value,
  onChange,
  label,
}: {
  value?: PlatformVersionRangeBound;
  onChange: (v: PlatformVersionRangeBound | undefined) => void;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <Label className="text-[10px] text-overlay1 w-6 shrink-0">{label}</Label>
      <Input
        placeholder="1.2.3"
        value={formatVersion(value?.version)}
        onChange={(e) => {
          const version = parseVersion(e.target.value);
          if (version) {
            onChange({ version, inclusive: value?.inclusive ?? true });
          } else if (e.target.value === "") {
            onChange(undefined);
          }
        }}
        className="h-8 text-xs font-mono"
      />
      <label className="flex items-center gap-1.5 text-[10px] text-overlay0 cursor-pointer shrink-0">
        <Checkbox
          checked={value?.inclusive ?? true}
          onCheckedChange={(checked) =>
            onChange({ version: value?.version, inclusive: !!checked })
          }
        />
        incl
      </label>
    </div>
  );
}

function PlatformRangeRow({
  value,
  onChange,
  onRemove,
}: {
  value: PlatformVersionRange;
  onChange: (v: PlatformVersionRange) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <PlatformVersionRangeBoundInput
        value={value.lower_bound}
        onChange={(v) => onChange({ ...value, lower_bound: v })}
        label="From"
      />
      <span className="text-overlay0 text-[10px]">to</span>
      <PlatformVersionRangeBoundInput
        value={value.upper_bound}
        onChange={(v) => onChange({ ...value, upper_bound: v })}
        label="To"
      />
      <Button type="button" variant="ghost" size="icon" onClick={onRemove} className="h-7 w-7 shrink-0">
        <X className="h-3 w-3 text-red" />
      </Button>
    </div>
  );
}

function SinglePlatformVersionEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: PlatformVersion;
  onChange: (v: PlatformVersion | undefined) => void;
}) {
  const pv: PlatformVersion = value ?? { ranges: [], work_around_pyoto_bug: false };

  const addRange = () => onChange({ ...pv, ranges: [...pv.ranges, {}] });
  const updateRange = (i: number, range: PlatformVersionRange) => {
    const ranges = [...pv.ranges];
    ranges[i] = range;
    onChange({ ...pv, ranges });
  };
  const removeRange = (i: number) =>
    onChange({ ...pv, ranges: pv.ranges.filter((_, j) => j !== i) });

  return (
    <div className="border border-surface1 rounded-md p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-subtext1">{label}</span>
        <label className="flex items-center gap-1.5 text-[10px] text-overlay0 cursor-pointer">
          <Checkbox
            checked={pv.work_around_pyoto_bug}
            onCheckedChange={(v) => onChange({ ...pv, work_around_pyoto_bug: !!v })}
          />
          pyoto bug
        </label>
      </div>
      <div className="space-y-1.5">
        {pv.ranges.map((range, i) => (
          <PlatformRangeRow
            key={i}
            value={range}
            onChange={(r) => updateRange(i, r)}
            onRemove={() => removeRange(i)}
          />
        ))}
      </div>
      <Button type="button" variant="ghost" size="sm" onClick={addRange} className="h-7 text-xs w-full border border-dashed border-surface1 hover:border-mauve/50">
        <Plus className="h-3 w-3 mr-1" />
        Add Range
      </Button>
    </div>
  );
}

export function PlatformVersionEditor({ value, onChange }: PlatformVersionEditorProps) {
  const [showRequiredChanges, setShowRequiredChanges] = useState(false);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <SinglePlatformVersionEditor
          label="iOS"
          value={value.ios_version}
          onChange={(v) => onChange({ ...value, ios_version: v })}
        />
        <SinglePlatformVersionEditor
          label="Android"
          value={value.android_version}
          onChange={(v) => onChange({ ...value, android_version: v })}
        />
        <SinglePlatformVersionEditor
          label="Web"
          value={value.web_version}
          onChange={(v) => onChange({ ...value, web_version: v })}
        />
        <SinglePlatformVersionEditor
          label="Native"
          value={value.native_version}
          onChange={(v) => onChange({ ...value, native_version: v })}
        />
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <Switch
          checked={value.allow_non_native_web}
          onCheckedChange={(checked) => onChange({ ...value, allow_non_native_web: checked })}
        />
        <span className="text-xs text-subtext0">Allow Non-Native Web</span>
      </label>

      <div className="border border-surface1 rounded-md">
        <button
          type="button"
          onClick={() => setShowRequiredChanges(!showRequiredChanges)}
          className="flex items-center gap-2 w-full px-3 py-2 text-xs text-subtext0 hover:text-text transition-colors"
        >
          {showRequiredChanges ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          <span className="font-medium">Client Required Changes</span>
          {(value.client_required_changes?.commit_hashes?.length ?? 0) > 0 ||
           (value.client_required_changes?.pr_numbers?.length ?? 0) > 0 ? (
            <span className="text-[10px] text-mauve bg-mauve/20 px-1.5 py-0.5 rounded">
              {(value.client_required_changes?.commit_hashes?.length ?? 0) +
               (value.client_required_changes?.pr_numbers?.length ?? 0)}
            </span>
          ) : null}
        </button>
        {showRequiredChanges && (
          <div className="px-3 pb-3 space-y-2 border-t border-surface1">
            <div className="flex gap-2 pt-2">
              <div className="flex-1">
                <Label className="text-[10px] text-overlay1 mb-1 block">Commit Hashes</Label>
                <TagInput
                  tags={value.client_required_changes?.commit_hashes ?? []}
                  onChange={(commit_hashes) =>
                    onChange({
                      ...value,
                      client_required_changes: {
                        commit_hashes,
                        pr_numbers: value.client_required_changes?.pr_numbers ?? [],
                      },
                    })
                  }
                  placeholder="abc123..."
                />
              </div>
              <div className="flex-1">
                <Label className="text-[10px] text-overlay1 mb-1 block">PR Numbers</Label>
                <TagInput
                  tags={value.client_required_changes?.pr_numbers?.map(String) ?? []}
                  onChange={(pr_numbers) =>
                    onChange({
                      ...value,
                      client_required_changes: {
                        commit_hashes: value.client_required_changes?.commit_hashes ?? [],
                        pr_numbers: pr_numbers.map(Number),
                      },
                    })
                  }
                  placeholder="1234..."
                  inputType="number"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TagInput({
  tags,
  onChange,
  placeholder,
  inputType = "text",
}: {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  inputType?: "text" | "number";
}) {
  const [inputValue, setInputValue] = useState("");

  const addTag = () => {
    const val = inputValue.trim();
    if (!val) return;
    if (inputType === "number") {
      const num = parseInt(val);
      if (!isNaN(num)) {
        onChange([...tags, String(num)]);
      }
    } else {
      onChange([...tags, val]);
    }
    setInputValue("");
  };

  const removeTag = (index: number) => {
    onChange(tags.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-1.5">
      <div className="flex gap-1.5">
        <Input
          type={inputType}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
          placeholder={placeholder}
          className="h-7 text-xs flex-1"
        />
        <Button type="button" variant="ghost" size="icon" onClick={addTag} className="h-7 w-7 shrink-0">
          <Plus className="h-3 w-3" />
        </Button>
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {tags.map((tag, i) => (
            <span key={i} className="inline-flex items-center gap-1 bg-surface0/50 text-subtext0 text-[10px] px-1.5 py-0.5 rounded">
              {tag}
              <button type="button" onClick={() => removeTag(i)} className="hover:text-red">
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
